import { createFileRoute, Link } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { MarketingLayout, SectionHeader, I18nPageHero } from "@/components/marketing/Layout";

export const Route = createFileRoute("/platform")({
  head: () => buildSeo({
    path: "/platform",
    title: "Reality Twin Network — the next layer of the internet",
    description: "Reality Twin Network (RTN) turns every company website into an intelligent AI Twin that talks to customers, partners and investors in real time.",
  }),
  component: PlatformPage,
});

function PlatformPage() {
  const { t } = useTranslation();
  const knows = t("platform.twin.knows", { returnObjects: true }) as string[];
  const shifts = t("platform.twin.shifts", { returnObjects: true }) as string[];
  const steps = t("platform.how.steps", { returnObjects: true }) as { n: string; title: string; desc: string }[];
  const caps = t("platform.how.caps", { returnObjects: true }) as string[];
  const finds = t("platform.network.finds", { returnObjects: true }) as string[];
  const ladder = t("platform.why.ladder", { returnObjects: true }) as string[];
  const lines = t("platform.closing.lines", { returnObjects: true }) as string[];

  return (
    <MarketingLayout>
      <I18nPageHero tKey="pages.platform" />

      {/* INTRO / PROBLEM */}
      <section className="border-b border-border py-20">
        <div className="mx-auto max-w-3xl px-6 space-y-6 text-[16.5px] leading-[1.7] text-muted-foreground">
          <p>{t("platform.intro.p1")}</p>
          <p>{t("platform.intro.p2")}</p>
          <p className="text-foreground font-medium">{t("platform.intro.p3")}</p>
        </div>
      </section>

      {/* THE AI TWIN */}
      <section className="bg-cream border-b border-border py-24">
        <div className="mx-auto max-w-6xl px-6">
          <SectionHeader
            eyebrow={t("platform.twin.eyebrow")}
            title={<>{t("platform.twin.titleA")} <em className="italic text-muted-foreground">{t("platform.twin.titleEm")}</em></>}
            sub={t("platform.twin.body")}
          />
          <div className="mt-14 grid lg:grid-cols-2 gap-px bg-border hairline rounded-xl overflow-hidden">
            <div className="bg-background p-8">
              <div className="font-mono-eyebrow">{t("platform.twin.knowsTitle")}</div>
              <ul className="mt-5 space-y-2.5">
                {knows.map((k) => (
                  <li key={k} className="flex items-center gap-3 text-[15px]">
                    <span className="h-1.5 w-1.5 rounded-full bg-foreground" />
                    {k}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-background p-8">
              <div className="font-mono-eyebrow">{t("platform.twin.shiftsTitle")}</div>
              <ul className="mt-5 space-y-4">
                {shifts.map((s) => (
                  <li key={s} className="flex gap-3 text-[15px] leading-relaxed">
                    <ArrowRight className="h-4 w-4 mt-1 shrink-0" strokeWidth={1.5} />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24">
        <div className="mx-auto max-w-6xl px-6">
          <SectionHeader
            eyebrow={t("platform.how.eyebrow")}
            title={<>{t("platform.how.titleA")} <em className="italic text-muted-foreground">{t("platform.how.titleEm")}</em></>}
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-border rounded-xl overflow-hidden">
            {steps.map((s) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5 }}
                className="bg-background p-7 flex flex-col min-h-[200px]"
              >
                <div className="font-mono-eyebrow">STEP {s.n}</div>
                <h3 className="mt-6 font-serif text-[24px] leading-tight">{s.title}</h3>
                <p className="mt-2 text-[14px] leading-relaxed text-muted-foreground">{s.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-14 hairline rounded-2xl bg-card p-8 sm:p-10">
            <div className="font-mono-eyebrow">{t("platform.how.capsTitle")}</div>
            <ul className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-[14.5px]">
              {caps.map((c) => (
                <li key={c} className="flex items-center gap-2.5">
                  <Check className="h-4 w-4" strokeWidth={2} />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* NETWORK */}
      <section className="bg-cream border-y border-border py-24">
        <div className="mx-auto max-w-6xl px-6">
          <SectionHeader
            eyebrow={t("platform.network.eyebrow")}
            title={<>{t("platform.network.titleA")} <em className="italic text-muted-foreground">{t("platform.network.titleEm")}</em></>}
            sub={t("platform.network.body")}
          />
          <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-border hairline rounded-xl overflow-hidden">
            {finds.map((f) => (
              <div key={f} className="bg-background p-6 text-center">
                <div className="font-serif text-[18px] leading-tight">{f}</div>
              </div>
            ))}
          </div>
          <p className="mt-10 max-w-2xl mx-auto text-center text-[16px] leading-[1.6] text-muted-foreground">
            {t("platform.network.close")}
          </p>
        </div>
      </section>

      {/* WHY RTN */}
      <section className="py-24">
        <div className="mx-auto max-w-6xl px-6">
          <SectionHeader
            eyebrow={t("platform.why.eyebrow")}
            title={<>{t("platform.why.titleA")} <em className="italic text-muted-foreground">{t("platform.why.titleEm")}</em></>}
          />
          <ol className="mt-14 max-w-2xl space-y-4">
            {ladder.map((l, i) => (
              <li key={l} className="flex items-baseline gap-5">
                <span className="font-mono text-[12px] text-muted-foreground tabular-nums">0{i + 1}</span>
                <span className={`font-serif text-[24px] sm:text-[28px] leading-tight ${i === ladder.length - 1 ? "text-foreground" : "text-muted-foreground"}`}>{l}</span>
              </li>
            ))}
          </ol>
          <p className="mt-10 max-w-2xl text-[16.5px] leading-[1.65] text-muted-foreground">
            {t("platform.why.body")}
          </p>
        </div>
      </section>

      {/* CLOSING / CTA */}
      <section className="bg-foreground text-background py-24 sm:py-28">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="font-serif text-[44px] sm:text-[64px] leading-[1.02] tracking-[-0.025em]">
            {t("platform.closing.titleA")} <em className="italic opacity-70">{t("platform.closing.titleEm")}</em>
          </h2>
          <div className="mt-10 space-y-3 text-[16px] sm:text-[17px] opacity-80">
            {lines.map((l) => <p key={l}>{l}</p>)}
          </div>
          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/auth" className="inline-flex items-center gap-1.5 rounded-full bg-background px-5 py-3 text-[14px] font-medium text-foreground hover:opacity-90 transition">
              {t("platform.closing.ctaPrimary")} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-1.5 rounded-full border border-background/30 px-5 py-3 text-[14px] font-medium hover:bg-background/10 transition">
              {t("platform.closing.ctaSecondary")}
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
